import { useEffect, useMemo, useRef, useState } from 'react';
import { STEPS, STEP_COLOR, STAGE_LABEL } from '@/lib/steps';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Video, Image as ImageIcon, Layout, Camera, Type, BookOpen, FileText,
  Copy, ExternalLink, Download, Play, Search, Filter, X, MessageSquare,
  Volume2, VolumeX,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import {
  qk, fetchCreatives, fetchClients, fetchStrategies, fetchAssets,
  type Creative, type MediaAsset,
} from '@/lib/queries';
import { cn } from '@/lib/cn';
import ReviewModal from '@/components/creative/ReviewModal';

const TYPE_ICON: Record<string, React.ReactNode> = {
  video: <Video size={20} />, reel: <Video size={20} />,
  static: <ImageIcon size={20} />, carousel: <Layout size={20} />,
  story: <BookOpen size={20} />, photo: <Camera size={20} />, copy: <Type size={20} />,
};

// Pleasant per-type gradient backgrounds (deterministic by code so each card is stable)
const TYPE_GRADIENT: Record<string, string> = {
  video:    'from-sky-900/40    via-blue-900/30   to-slate-950',
  reel:     'from-fuchsia-900/40 via-purple-900/30 to-slate-950',
  static:   'from-emerald-900/40 via-teal-900/30   to-slate-950',
  carousel: 'from-orange-900/40 via-amber-900/30   to-slate-950',
  story:    'from-rose-900/40    via-pink-900/30   to-slate-950',
  photo:    'from-cyan-900/40    via-sky-900/30    to-slate-950',
  copy:     'from-yellow-900/40 via-orange-900/30  to-slate-950',
};

// The step vocabulary comes from lib/steps.ts, which the API owns, so this page
// cannot drift from the tracker, the calendar or the creatives list.
const STEP_PILLS: Array<[string, string]> = [['', 'All steps'], ...STEPS.map((s) => [s.key, s.label] as [string, string])];

const STAGE_COLOR: Record<string, string> = {
  brief:        'bg-slate-700/80 text-slate-200',
  shoot:        'bg-blue-600/80 text-white',
  raws_review:  'bg-yellow-600/80 text-white',
  edit:         'bg-purple-600/80 text-white',
  dual_review:  'bg-orange-600/80 text-white',
  done:         'bg-emerald-600/80 text-white',
  posted:       'bg-accent-600/80 text-white',
};

export default function LibraryPage() {
  const [reviewing, setReviewing] = useState<MediaAsset | null>(null);
  // Filters live in the address, not in component state, so leaving for a creative
  // and pressing back brings them back with you. They used to reset to empty, which
  // silently widened the page to every clinic.
  const [params, setParams] = useSearchParams();
  const param = (k: string) => params.get(k) ?? '';
  const setParam = (k: string, v: string) => setParams((cur) => {
    const next = new URLSearchParams(cur);
    if (v) next.set(k, v); else next.delete(k);
    return next;
  }, { replace: true });
  const q = param('q');
  const setQ = (v: string) => setParam('q', v);
  const clientId = param('client');
  const setClientId = (v: string) => setParam('client', v);
  const strategyId = param('strategy');
  const setStrategyId = (v: string) => setParam('strategy', v);
  const creativeType = param('type');
  const setCreativeType = (v: string) => setParam('type', v);
  const funnel = param('funnel');
  const setFunnel = (v: string) => setParam('funnel', v);
  const step = param('step');
  const setStep = (v: string) => setParam('step', v);
  const primary = param('dept');
  const setPrimary = (v: string) => setParam('dept', v);
  const secondary = param('treatment');
  const setSecondary = (v: string) => setParam('treatment', v);
  const channel = param('channel');
  const setChannel = (v: string) => setParam('channel', v);
  const talent = param('doctor');
  const setTalent = (v: string) => setParam('doctor', v);
  const [qSent, setQSent] = useState('');
  useEffect(() => { const t = setTimeout(() => setQSent(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const clients = useQuery({ queryKey: qk.clients, queryFn: fetchClients });
  const strategies = useQuery({ queryKey: qk.strategies, queryFn: fetchStrategies });

  const filterParams = {
    client_id: clientId, strategy_id: strategyId,
    creative_type: creativeType, funnel_stage: funnel, step,
    q: qSent, talent_id: talent, taxonomy_primary: primary, taxonomy_secondary: secondary,
  };
  const creatives = useQuery({
    queryKey: qk.creativesFiltered(filterParams),
    queryFn: () => fetchCreatives(filterParams),
  });

  // Derive label vocab from the picked client. If only a strategy is picked,
  // derive the client from it (strategy belongs to a client). Defaults are
  // neutral generics so we never show "Specialty" outside a clinic context.
  const pickedStrategy = (strategies.data ?? []).find((s) => s.id === strategyId);
  const effectiveClientId = clientId || pickedStrategy?.client_id || '';
  const pickedClient = (clients.data ?? []).find((c) => c.id === effectiveClientId);
  const primaryLabel   = pickedClient?.taxonomy.primary.label   ?? 'Category';
  const secondaryLabel = pickedClient?.taxonomy.secondary.label ?? 'Sub-category';
  const talentLabel    = pickedClient?.taxonomy.talent.label    ?? 'Talent';

  // Available filter values — derived from currently-loaded set so we don't show empty buckets
  const distinctValues = useMemo(() => {
    const list = creatives.data ?? [];
    const primaries = new Set<string>();
    const secondaries = new Set<string>();
    const channels = new Set<string>();
    const talents = new Map<string, string>(); // id -> name
    for (const c of list) {
      if (c.taxonomy_primary)   primaries.add(c.taxonomy_primary);
      if (c.taxonomy_secondary) secondaries.add(c.taxonomy_secondary);
      if (c.talent_id && c.talent_name) talents.set(c.talent_id, c.talent_name);
      for (const ch of c.channels ?? []) channels.add(ch);
    }
    return {
      primaries:   Array.from(primaries).sort(),
      secondaries: Array.from(secondaries).sort(),
      channels:    Array.from(channels).sort(),
      talents:     Array.from(talents.entries()).map(([id, name]) => ({ id, name })),
    };
  }, [creatives.data]);

  // A picked clinic knows its own departments, treatments and doctors. Deriving the
  // options from the creatives on screen emptied a dropdown as soon as another filter
  // narrowed the page, which read as the filter disappearing.
  const options = useMemo(() => ({
    primaries: pickedClient?.taxonomy.primary.values?.length
      ? [...pickedClient.taxonomy.primary.values].sort() : distinctValues.primaries,
    secondaries: pickedClient?.taxonomy.secondary.values?.length
      ? [...pickedClient.taxonomy.secondary.values].sort() : distinctValues.secondaries,
    talents: pickedClient?.talents?.length
      ? pickedClient.talents.map((t) => ({ id: t.id, name: t.name })) : distinctValues.talents,
    channels: distinctValues.channels,
  }), [pickedClient, distinctValues]);

  // Search, doctor and both taxonomy axes are applied by the server now, over every
  // creative rather than the loaded page. Only the channel stays here: it is an array
  // column and no one has asked to filter the whole library by it.
  const filtered = useMemo(() => {
    const list = creatives.data ?? [];
    if (!channel) return list;
    return list.filter((c) => (c.channels ?? []).includes(channel));
  }, [creatives.data, channel]);

  const activeFilterCount =
    [clientId, strategyId, creativeType, funnel, step, primary, secondary, channel, talent, q.trim()]
      .filter(Boolean).length;

  function resetFilters() {
    setQ(''); setClientId(''); setStrategyId(''); setCreativeType('');
    setFunnel(''); setStep(''); setPrimary(''); setSecondary(''); setChannel(''); setTalent('');
  }

  return (
    <div>
      <PageHeader
        title="Creatives Library"
        subtitle="Every creative, finished and in progress, searchable by client, doctor, treatment and channel."
      />

      {/* Filter bar - solid bg (no backdrop-blur, which creates a stacking context that fights the Select dropdown z-index) */}
      <div className="relative z-20 rounded-xl border border-slate-800 bg-bg-card p-4 mb-5 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search title, code, doctor, treatment..." />
          <Select label="Marketing strategy" value={strategyId} onChange={setStrategyId} searchable
            options={[
              { value: '', label: 'All strategies' },
              ...(strategies.data ?? []).map((s) => ({ value: s.id, label: s.title, hint: s.code })),
            ]} />
          <Select label="Client" value={clientId} onChange={(v) => { setClientId(v); setPrimary(''); setTalent(''); }} searchable
            options={[
              { value: '', label: 'All clients' },
              ...(clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]} />
          <Select label={primaryLabel} value={primary} onChange={setPrimary}
            options={[
              { value: '', label: `All ${primaryLabel.toLowerCase()}` },
              ...options.primaries.map((v) => ({ value: v, label: v })),
            ]} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <Select label={secondaryLabel} value={secondary} onChange={setSecondary}
            options={[
              { value: '', label: `All ${secondaryLabel.toLowerCase()}` },
              ...options.secondaries.map((v) => ({ value: v, label: v })),
            ]} />
          <Select label="Type" value={creativeType} onChange={setCreativeType}
            options={[
              { value: '', label: 'All types' },
              ...['video','reel','static','carousel','story','photo','copy'].map((t) => ({ value: t, label: t })),
            ]} />
          <Select label="Funnel" value={funnel} onChange={setFunnel}
            options={[
              { value: '', label: 'All funnel' },
              { value: 'TOF', label: 'TOF (top of funnel)' },
              { value: 'MOF', label: 'MOF (middle)' },
              { value: 'BOF', label: 'BOF (bottom)' },
            ]} />
          <Select label="Channel" value={channel} onChange={setChannel}
            options={[
              { value: '', label: 'All channels' },
              ...options.channels.map((v) => ({ value: v, label: v.replace(/_/g, ' ') })),
            ]} />
          <Select label={talentLabel} value={talent} onChange={setTalent} searchable
            options={[
              { value: '', label: `All ${talentLabel.toLowerCase()}s` },
              ...options.talents.map((t) => ({ value: t.id, label: t.name })),
            ]} />
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-800">
          <div className="text-xs text-muted inline-flex items-center gap-2">
            <Filter size={11} />
            {activeFilterCount === 0 ? 'No filters' : `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} active`}
            <span className="text-muted/60">·</span>
            <span>{filtered.length} of {creatives.data?.length ?? 0} creatives</span>
          </div>
          {activeFilterCount > 0 && (
            <button onClick={resetFilters} className="text-xs text-accent-400 hover:underline inline-flex items-center gap-1">
              <X size={11} /> Reset
            </button>
          )}
        </div>
      </div>

      {/* One vocabulary: the same steps the tracker groups by and the calendar colours */}
      <div className="flex flex-wrap gap-2 mb-5">
        {STEP_PILLS.map(([value, label]) => (
          <button key={value || 'all'} onClick={() => setStep(value)}
            className={cn(
              'text-[11px] px-3 py-1.5 min-h-[32px] rounded-full border transition',
              step === value
                ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                : 'border-slate-800 text-muted hover:text-muted-strong'
            )}>
            {label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {creatives.isLoading && <div className="text-muted text-sm">Loading...</div>}
      {!creatives.isLoading && filtered.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-bg-card/40 py-16 text-center text-muted text-sm">
          No creatives match the current filters.
          {activeFilterCount > 0 && (
            <button onClick={resetFilters} className="block mt-3 mx-auto text-xs text-accent-400 underline">
              Reset filters
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((c) => <LibraryCard key={c.id} c={c} onReview={setReviewing} />)}
      </div>

      {reviewing && (
        <ReviewModal asset={reviewing} onClose={() => setReviewing(null)} />
      )}
    </div>
  );
}

function LibraryCard({ c, onReview }: { c: Creative; onReview: (a: MediaAsset) => void }) {
  async function openReview() {
    if (!c.latest_asset_url) {
      toast.error('No assets uploaded yet - open the creative to add one');
      return;
    }
    // Pull the latest asset row so the modal has full metadata
    try {
      const assets = await fetchAssets(c.id);
      const cover = assets.find(a => a.storage_path === c.latest_asset_storage_path) ?? assets[0];
      if (!cover) {
        toast.error('No assets found');
        return;
      }
      onReview(cover);
    } catch {
      toast.error('Could not load asset');
    }
  }

  function copy() {
    const slug = `${c.code} - ${c.title}`;
    navigator.clipboard.writeText(slug)
      .then(() => toast.success('Code + title copied'))
      .catch(() => toast.error('Copy failed'));
  }

  const finalUrl = c.posted_url || null;
  const grad = TYPE_GRADIENT[c.creative_type] ?? 'from-slate-800 to-slate-950';
  const thumb = c.latest_asset_thumbnail_url || null;
  const playable = c.latest_asset_url || null;
  const isVideoCover = playable && c.latest_asset_content_type?.startsWith('video/');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Per-card mute state. Defaults muted (browsers block autoplay-with-sound, and
  // hovering through a grid shouldn't blast audio). User can unmute one card at a
  // time - that card keeps playing with sound until hover leaves.
  const [muted, setMuted] = useState(true);

  function handleHoverPlay() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    v.play().catch(() => { /* autoplay blocked - silent */ });
  }
  function handleHoverPause() {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    try { v.currentTime = 0.1; } catch { /* ignored */ }
    // Reset mute state when leaving so the next hover starts fresh (silent)
    setMuted(true);
  }
  function toggleMute(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    setMuted(next);
    v.muted = next;
    if (!next) v.play().catch(() => { /* */ });
  }

  return (
    <article
      className="always-dark group relative rounded-xl overflow-hidden border border-slate-800 bg-bg shadow-card hover:shadow-cardHover transition aspect-[9/16]"
      onMouseEnter={isVideoCover ? handleHoverPlay : undefined}
      onMouseLeave={isVideoCover ? handleHoverPause : undefined}
    >
      {/* Cover: hover-to-play video, else thumbnail image, else gradient placeholder */}
      {isVideoCover ? (
        <>
          <video
            ref={videoRef}
            src={`${playable}#t=0.1`}
            poster={thumb ?? undefined}
            preload="metadata"
            muted={muted}
            playsInline
            loop
            className="absolute inset-0 w-full h-full object-cover bg-black"
          />
          {/* Sound toggle - only meaningful while hover-playing, fades in on hover */}
          <button
            onClick={toggleMute}
            className={cn(
              'absolute bottom-2 right-2 z-20 w-8 h-8 rounded-full backdrop-blur flex items-center justify-center transition opacity-0 group-hover:opacity-100',
              muted ? 'bg-black/60 text-white hover:bg-black/80' : 'bg-accent-500 text-white hover:bg-accent-400 ring-2 ring-white/40',
            )}
            title={muted ? 'Play with sound' : 'Mute'}
          >
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
        </>
      ) : thumb ? (
        <img
          src={thumb}
          alt={c.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <>
          <div className={cn('absolute inset-0 bg-gradient-to-br', grad)} />
          <div className="absolute inset-0 flex items-center justify-center text-white/10">
            <div className="scale-[8]">{TYPE_ICON[c.creative_type]}</div>
          </div>
        </>
      )}

      {/* Top gradient + badges */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />
      <div className="absolute top-2.5 left-2.5 right-2.5 z-10 flex items-start justify-between">
        <span className="bg-bg-card/90 backdrop-blur text-[10px] font-mono px-2 py-1 rounded-full text-muted-strong">
          {c.code}
        </span>
        <span className={cn('text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border', STEP_COLOR[c.step_key ?? ''] ?? STAGE_COLOR[c.current_stage] ?? STAGE_COLOR.brief)}>
          {c.step_label ?? STAGE_LABEL[c.current_stage] ?? c.current_stage}
        </span>
      </div>

      {/* Center play (if posted) */}
      {finalUrl && (
        <a
          href={finalUrl} target="_blank" rel="noreferrer"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-accent-500 hover:bg-accent-400 flex items-center justify-center text-white transition shadow-lg group-hover:scale-110"
          aria-label="Open posted URL"
        >
          <Play size={20} className="ml-0.5" />
        </a>
      )}

      {/* Bottom gradient + content */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/95 via-black/60 to-transparent pointer-events-none" />

      <div className="absolute inset-x-0 bottom-0 z-10 p-3 space-y-2">
        {/* Tags row */}
        <div className="flex flex-wrap gap-1 text-[9px]">
          <Tag>{c.creative_type}</Tag>
          <Tag>{c.funnel_stage}</Tag>
          {c.is_paid && <Tag color="orange">Paid</Tag>}
          {c.client_visible && <Tag color="emerald">Client</Tag>}
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-white line-clamp-2 leading-tight">
          {c.title}
        </h3>

        {/* Subtitle */}
        <div className="text-[10px] text-white/70 line-clamp-1">
          {c.client_name}
          {c.taxonomy_primary && ` · ${c.taxonomy_primary}`}
          {c.taxonomy_secondary && ` / ${c.taxonomy_secondary}`}
          {c.talent_name && ` · ${c.talent_name}`}
        </div>

        {/* Date strip */}
        {(c.posting_date || c.execution_date) && (
          <div className="text-[10px] text-white/50">
            {c.posting_date ? `Post ${c.posting_date}` : `Shoot ${c.execution_date}`}
          </div>
        )}

        {/* Action pills */}
        <div className="flex gap-1.5 pt-1.5">
          <button onClick={openReview} disabled={!c.latest_asset_url}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[10px] px-2 py-1.5 rounded-full bg-accent-500 hover:bg-accent-400 disabled:bg-white/10 disabled:text-white/40 text-white transition font-medium"
            title="Mark up + comment on the latest asset">
            <MessageSquare size={10} /> Review
          </button>
          <Link to={`/creatives/${c.id}`}
            className="inline-flex items-center justify-center gap-1 text-[10px] px-2 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            title="Open creative detail">
            <ExternalLink size={10} />
          </Link>
          <button onClick={copy}
            className="inline-flex items-center justify-center gap-1 text-[10px] px-2 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            title="Copy code + title">
            <Copy size={10} />
          </button>
          {thumb ? (
            <a href={thumb} target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-1 text-[10px] px-2 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
              title="Open latest asset">
              <Download size={10} />
            </a>
          ) : (
            <button disabled
              className="inline-flex items-center justify-center gap-1 text-[10px] px-2 py-1.5 rounded-full bg-white/5 text-white/30 cursor-not-allowed"
              title="No asset uploaded yet">
              <Download size={10} />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function Tag({ children, color = 'slate' }: { children: React.ReactNode; color?: 'slate' | 'orange' | 'emerald' }) {
  const cls = {
    slate:   'bg-white/10 text-white/80',
    orange:  'bg-orange-500/30 text-orange-200',
    emerald: 'bg-emerald-500/30 text-emerald-200',
  }[color];
  return (
    <span className={cn('uppercase tracking-wider px-1.5 py-0.5 rounded backdrop-blur', cls)}>
      {children}
    </span>
  );
}

function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (s: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <span className="block text-xs text-muted mb-1.5">Search</span>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
        <input
          value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-800 bg-bg text-sm text-muted-strong placeholder:text-muted/50 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400/40"
        />
      </div>
    </label>
  );
}
